import React, { useRef, useState } from 'react';
import HackathonTrendChart from './HackathonTrendChart';
import './HackathonSurveyAnalytics.css';

const BACKGROUND_LABELS = {
  student: 'Student',
  employed: 'Employed',
  entrepreneur: 'Entrepreneur',
  other: 'Other',
};

const FACILITATOR_ORDER = ['very_poor', 'poor', 'good', 'very_good', 'outstanding'];
const FACILITATOR_LABELS = {
  very_poor: 'Very Poor',
  poor: 'Poor',
  good: 'Good',
  very_good: 'Very Good',
  outstanding: 'Outstanding',
};
const FACILITATOR_FILL = {
  very_poor: 'hackathon-bar-fill-no',
  poor: 'hackathon-bar-fill-no',
  good: 'hackathon-bar-fill-undecided',
  very_good: 'hackathon-bar-fill-yes',
  outstanding: 'hackathon-bar-fill-yes',
};

function ratingTextClass(rating) {
  if (rating >= 8) return 'badge-positive-text';
  if (rating >= 5) return 'badge-pending-text';
  if (rating > 0) return 'badge-negative-text';
  return '';
}

function CountBars({ entries, fillClass }) {
  if (!entries || entries.length === 0) {
    return <p style={{ color: '#666', fontSize: '0.85rem' }}>No data yet.</p>;
  }
  const max = Math.max(...entries.map((e) => e.count), 1);
  return (
    <div>
      {entries.map((entry) => (
        <div key={entry.name} className="hackathon-bar-row">
          <div title={entry.name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {entry.label || entry.name}
          </div>
          <div className="hackathon-bar-track">
            <div
              className={`hackathon-bar-fill ${entry.fillClass || fillClass || ''}`}
              style={{ width: `${(entry.count / max) * 100}%` }}
            />
          </div>
          <div className="hackathon-bar-value">{entry.count}</div>
        </div>
      ))}
    </div>
  );
}

function RatingBars({ breakdown }) {
  const ratings = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const counts = ratings.map((r) => breakdown?.[String(r)] || 0);
  const max = Math.max(...counts, 1);
  return (
    <div>
      {ratings.map((r, idx) => (
        <div key={r} className="hackathon-bar-row">
          <div>{r} / 10</div>
          <div className="hackathon-bar-track">
            <div
              className="hackathon-bar-fill hackathon-bar-fill-rating"
              style={{ width: `${(counts[idx] / max) * 100}%` }}
            />
          </div>
          <div className="hackathon-bar-value">{counts[idx]}</div>
        </div>
      ))}
    </div>
  );
}

function OrdinalBars({ breakdown, order, labels, fillMap }) {
  const counts = order.map((key) => breakdown?.[key] || 0);
  const max = Math.max(...counts, 1);
  return (
    <div>
      {order.map((key, idx) => (
        <div key={key} className="hackathon-bar-row">
          <div>{labels[key] || key}</div>
          <div className="hackathon-bar-track">
            <div
              className={`hackathon-bar-fill ${fillMap[key] || ''}`}
              style={{ width: `${(counts[idx] / max) * 100}%` }}
            />
          </div>
          <div className="hackathon-bar-value">{counts[idx]}</div>
        </div>
      ))}
    </div>
  );
}

function entriesFrom(obj, labelMap = {}) {
  if (!obj) return [];
  return Object.entries(obj)
    .map(([name, count]) => ({ name, count, label: labelMap[name] || name.replace(/_/g, ' ') }))
    .sort((a, b) => b.count - a.count);
}

export default function HackathonSurveyAnalytics({ analytics }) {
  const cardRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const handleDownloadPdf = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    setExportError(null);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height / canvas.width) * imgWidth;

      // Paginate when the rendered panel is taller than one A4 page.
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save('hackathon_survey_analytics.pdf');
    } catch (e) {
      setExportError(e.message || 'PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  if (!analytics) return null;

  const {
    total = 0,
    avg_overall_experience: avgExperience = 0,
    avg_team_rating: avgTeam = 0,
    overall_experience_breakdown: experienceBreakdown = {},
    team_rating_breakdown: teamBreakdown = {},
    background_breakdown: backgroundBreakdown = {},
    facilitator_support_breakdown: facilitatorBreakdown = {},
    outcome_breakdown: outcomeBreakdown = {},
    highlight_count: highlightCount = 0,
    daily_trend: dailyTrend = [],
  } = analytics;

  const backgroundEntries = entriesFrom(backgroundBreakdown, BACKGROUND_LABELS);
  const outcomeEntries = entriesFrom(outcomeBreakdown);

  return (
    <div className="card hackathon-survey-analytics" ref={cardRef}>
      <div className="hackathon-analytics-header">
        <h3>Hackathon Survey Analytics</h3>
        <button
          className="btn-secondary"
          onClick={handleDownloadPdf}
          disabled={exporting}
          data-html2canvas-ignore="true"
        >
          {exporting ? 'Preparing PDF...' : 'Download PDF'}
        </button>
      </div>
      {exportError && (
        <div className="error-message" style={{ marginBottom: '0.5rem' }} data-html2canvas-ignore="true">
          PDF export failed: {exportError}
        </div>
      )}

      <div className="grid grid-4" style={{ marginBottom: '1rem', marginTop: '1rem' }}>
        <div className="analytics-stat">
          <div className="stat-value">{total}</div>
          <div className="stat-label">Total Responses</div>
        </div>
        <div className="analytics-stat">
          <div className={`stat-value ${ratingTextClass(avgExperience)}`}>
            {avgExperience > 0 ? avgExperience.toFixed(2) : '—'}
            <span className="stat-scale"> / 10</span>
          </div>
          <div className="stat-label">Avg Overall Experience</div>
        </div>
        <div className="analytics-stat">
          <div className={`stat-value ${ratingTextClass(avgTeam)}`}>
            {avgTeam > 0 ? avgTeam.toFixed(2) : '—'}
            <span className="stat-scale"> / 10</span>
          </div>
          <div className="stat-label">Avg Team Rating</div>
        </div>
        <div className="analytics-stat">
          <div className="stat-value">{highlightCount}</div>
          <div className="stat-label">Highlights Shared</div>
        </div>
      </div>

      <div className="hackathon-section">
        <h4>Trend Over Time</h4>
        <HackathonTrendChart trend={dailyTrend} />
      </div>

      <div className="hackathon-section">
        <h4>Overall Experience Distribution</h4>
        <RatingBars breakdown={experienceBreakdown} />
      </div>

      <div className="hackathon-section">
        <h4>Team Rating Distribution</h4>
        <RatingBars breakdown={teamBreakdown} />
      </div>

      <div className="hackathon-section">
        <h4>Participant Background</h4>
        <CountBars entries={backgroundEntries} />
      </div>

      <div className="hackathon-section">
        <h4>Facilitator Support</h4>
        <OrdinalBars
          breakdown={facilitatorBreakdown}
          order={FACILITATOR_ORDER}
          labels={FACILITATOR_LABELS}
          fillMap={FACILITATOR_FILL}
        />
      </div>

      <div className="hackathon-section">
        <h4>Call Outcomes</h4>
        <div className="hackathon-breakdown-badges">
          {outcomeEntries.length === 0 ? (
            <span style={{ color: '#666', fontSize: '0.85rem' }}>No data yet.</span>
          ) : (
            outcomeEntries.map((entry) => (
              <span key={entry.name} className="badge badge-neutral">
                {entry.label}: {entry.count}
              </span>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
